import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getEjercicio, getFlujoCaja } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import useModoAsiento from "@/lib/useModoAsiento";
import { SubcuentasDesglose } from "@/components/analisis/FlujoCajaContent";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Construcción del EFE (vista visual, SOLO LECTURA): waterfall de tesorería
// inicial → CFO → CFI → CFF → tesorería final, y el desglose completo de cada
// apunte (fórmula, datos utilizados y subcuentas, que desde 2026-09 siempre
// suman la línea). Nivel 1 del "EFE configurable": ver cómo se construye; la
// edición del criterio (mover partidas de bloque, método) será una fase aparte.
// ---------------------------------------------------------------------------

const BLOQUES = [
  {
    clave: "cfo",
    titulo: "A) Cash Flow Operativo (CFO)",
    descripcion: "Resultado del ejercicio ± variaciones del circulante operativo",
    items: [
      ["resultado", "Resultado del ejercicio"],
      ["amortizacion", "Amortización del inmovilizado (+)"],
      ["impuesto_diferido", "Impuesto diferido (474/479)"],
      ["existencias", "Existencias (impacto caja)"],
      ["clientes", "Clientes (impacto caja)"],
      ["proveedores", "Proveedores (impacto caja)"],
      ["periodificaciones_activo", "Periodificaciones de activo"],
      ["periodificaciones_pasivo", "Periodificaciones de pasivo"],
      ["provisiones", "Provisiones"],
    ],
  },
  {
    clave: "cfi",
    titulo: "B) Cash Flow Inversión (CFI)",
    descripcion: "Inversión en activos: Δ inmovilizado neto + amortización del ejercicio (CAPEX bruto, F7)",
    items: [
      ["inmovilizado", "Inmovilizado (intangible + material)"],
      ["inv_inmobiliarias", "Inversiones inmobiliarias"],
      ["empresas_grupo", "Empresas del grupo"],
      ["inv_financieras", "Inversiones financieras"],
    ],
  },
  {
    clave: "cff",
    titulo: "C) Cash Flow Financiero (CFF)",
    descripcion: "Operaciones con socios y variación de la deuda",
    items: [
      ["patrimonio", "Patrimonio neto (ampliaciones / dividendos / subvenciones)"],
      ["deuda", "Deuda (nueva − amortizada)"],
      ["deuda_grupo", "Deudas con empresas del grupo"],
    ],
  },
];

function etiquetaDetalle(clave) {
  let txt = clave.replace(/_t1$/, " (año anterior)").replace(/_t$/, " (año actual)");
  txt = txt.replace(/_/g, " ");
  return txt.charAt(0).toUpperCase() + txt.slice(1);
}

// Waterfall en SVG puro: barras absolutas (tesorerías) y flotantes (bloques).
function Waterfall({ pasos }) {
  const W = 760, H = 280, PAD = 36, GAP = 18;
  const n = pasos.length;
  const barW = (W - PAD * 2 - GAP * (n - 1)) / n;

  // Rango del eje: acumulados y 0.
  let vals = [0];
  pasos.forEach((p) => { vals.push(p.desde); vals.push(p.hasta); });
  const min = Math.min(...vals), max = Math.max(...vals);
  const rango = max - min || 1;
  const y = (v) => 24 + ((max - v) / rango) * (H - 70);

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[560px]">
        {/* línea del cero */}
        <line x1={PAD} x2={W - PAD} y1={y(0)} y2={y(0)} stroke="currentColor" strokeOpacity="0.25" strokeDasharray="4 4" />
        {pasos.map((p, i) => {
          const x = PAD + i * (barW + GAP);
          const y1 = y(Math.max(p.desde, p.hasta));
          const alto = Math.max(2, Math.abs(y(p.desde) - y(p.hasta)));
          const positivo = p.hasta >= p.desde;
          const color = p.tipo === "total"
            ? "hsl(222 47% 40%)"
            : positivo ? "hsl(152 60% 40%)" : "hsl(350 70% 50%)";
          return (
            <g key={p.nombre}>
              {/* conector con la barra anterior */}
              {i > 0 && (
                <line
                  x1={x - GAP} x2={x}
                  y1={y(p.desde)} y2={y(p.desde)}
                  stroke="currentColor" strokeOpacity="0.3"
                />
              )}
              <rect x={x} y={y1} width={barW} height={alto} rx="3" fill={color} fillOpacity="0.85" />
              <text x={x + barW / 2} y={y1 - 6} textAnchor="middle" fontSize="11" fontWeight="600" fill="currentColor">
                {p.tipo === "total"
                  ? formatCurrency(p.hasta)
                  : `${p.hasta - p.desde >= 0 ? "+" : ""}${formatCurrency(p.hasta - p.desde)}`}
              </text>
              <text x={x + barW / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="currentColor" fillOpacity="0.7">
                {p.nombre}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ApunteEfe({ label, data, onVerMayor, anio, anioAnterior }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const valor = data.valor || 0;
  const tieneContenido = (data.subcuentas || []).length > 0 || data.detalle;
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/50 cursor-pointer">
          <div className="flex items-center gap-2 min-w-0">
            {tieneContenido ? (
              open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                   : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : <span className="w-4 shrink-0" />}
            <span className="text-sm truncate">{label}</span>
            {data.formula && (
              <Badge variant="outline" className="font-mono text-[11px] shrink-0 hidden sm:inline-flex">
                {data.formula}
              </Badge>
            )}
          </div>
          <span className={`font-mono tabular-nums text-sm shrink-0 ${valor > 0 ? "text-emerald-600" : valor < 0 ? "text-rose-600" : "text-muted-foreground"}`}>
            {valor > 0 ? "+" : ""}{formatCurrency(valor)}
          </span>
        </div>
      </CollapsibleTrigger>
      {tieneContenido && (
        <CollapsibleContent>
          <div className="ml-6 mb-3 space-y-3">
            {data.explicacion && (
              <p className="text-xs text-muted-foreground italic">{data.explicacion}</p>
            )}
            {data.detalle && (
              <div className="border rounded-lg divide-y text-sm max-w-md">
                {Object.entries(data.detalle).map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center px-3 py-1.5">
                    <span className="text-muted-foreground text-xs">{etiquetaDetalle(k)}</span>
                    <span className="font-mono text-xs tabular-nums">{formatCurrency(v)}</span>
                  </div>
                ))}
              </div>
            )}
            {(data.subcuentas || []).length > 0 && (
              <SubcuentasDesglose
                subcuentas={data.subcuentas}
                anio={anio}
                anioAnterior={anioAnterior}
                onVerMayor={onVerMayor}
                impactoCaja={valor}
              />
            )}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

export default function ConstruccionEfePage() {
  const { ejercicioId } = useParams();
  const navigate = useNavigate();
  const { modoAsiento, irAlMayor } = useModoAsiento(ejercicioId);

  const [flujo, setFlujo] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    (async () => {
      setCargando(true);
      try {
        const { data } = await getFlujoCaja(ejercicioId);
        setFlujo(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error al cargar el flujo de caja");
      } finally {
        setCargando(false);
      }
    })();
  }, [ejercicioId]);

  const goBack = async () => {
    navigate(`/ejercicios/${ejercicioId}/analisis?tab=flujos`);
  };

  if (cargando) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!flujo || flujo.error) {
    // Sin ejercicio anterior el backend responde 200 con {error, mensaje}
    // (flujo_caja.py): no es un flujo, es el motivo de que no lo haya.
    return (
      <div className="space-y-4 max-w-3xl mx-auto">
        <Button variant="ghost" size="icon" onClick={goBack}><ArrowLeft className="h-4 w-4" /></Button>
        <Card><CardContent className="pt-6 text-sm text-muted-foreground">
          {flujo?.mensaje || "No hay flujo de caja para este ejercicio (hace falta el año anterior con datos)."}
        </CardContent></Card>
      </div>
    );
  }

  const c = flujo.conciliacion || {};
  const pasos = [
    { nombre: `Tesorería ${flujo.anio_anterior ?? "inicial"}`, desde: 0, hasta: c.saldo_inicial || 0, tipo: "total" },
    { nombre: "CFO", desde: c.saldo_inicial || 0, hasta: (c.saldo_inicial || 0) + (flujo.cfo?.total || 0) },
    { nombre: "CFI", desde: (c.saldo_inicial || 0) + (flujo.cfo?.total || 0), hasta: (c.saldo_inicial || 0) + (flujo.cfo?.total || 0) + (flujo.cfi?.total || 0) },
    { nombre: "CFF", desde: (c.saldo_inicial || 0) + (flujo.cfo?.total || 0) + (flujo.cfi?.total || 0), hasta: c.saldo_final_calculado || 0 },
    { nombre: `Tesorería ${flujo.anio ?? "final"}`, desde: 0, hasta: c.saldo_final_calculado || 0, tipo: "total" },
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={goBack} data-testid="efe-volver">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="page-title">
            Construcción del EFE {flujo.anio}
          </h1>
          <p className="text-sm text-muted-foreground">
            Cómo se llega de la tesorería de {flujo.anio_anterior} a la de {flujo.anio}. Solo
            lectura: todos los importes salen del balance y del PyG.
          </p>
        </div>
        <Badge
          variant="outline"
          className={c.check_ok ? "text-emerald-600 border-emerald-600" : "text-rose-600 border-rose-600"}
        >
          {c.check_ok
            ? <><CheckCircle2 className="h-3 w-3 mr-1" /> Concilia a 0,00</>
            : <><AlertCircle className="h-3 w-3 mr-1" /> Diferencia: {formatCurrency(c.diferencia)}</>}
        </Badge>
      </div>

      {/* Waterfall */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>El puente de tesorería</CardTitle>
          <CardDescription>
            Tesorería inicial {formatCurrency(c.saldo_inicial)} + CFO + CFI + CFF ={" "}
            {formatCurrency(c.saldo_final_calculado)} (= tesorería del balance,{" "}
            {formatCurrency(c.tesoreria_balance)})
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Waterfall pasos={pasos} />
        </CardContent>
      </Card>

      {/* Bloques con desglose completo */}
      {BLOQUES.map(({ clave, titulo, descripcion, items }) => {
        const bloque = flujo[clave] || {};
        return (
          <Card key={clave} data-testid={`efe-bloque-${clave}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <CardTitle>{titulo}</CardTitle>
                  <CardDescription>{descripcion}</CardDescription>
                </div>
                <span className={`font-mono font-semibold tabular-nums ${
                  (bloque.total || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {(bloque.total || 0) > 0 ? "+" : ""}{formatCurrency(bloque.total)}
                </span>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="divide-y">
                {items.map(([k, label]) => (
                  <ApunteEfe
                    key={k}
                    label={label}
                    data={bloque[k]}
                    onVerMayor={modoAsiento ? irAlMayor : null}
                    anio={flujo.anio}
                    anioAnterior={flujo.anio_anterior}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Conciliación */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="rounded-md border p-3">
              <p className="text-muted-foreground text-xs">Tesorería inicial ({flujo.anio_anterior})</p>
              <p className="font-mono font-semibold tabular-nums">{formatCurrency(c.saldo_inicial)}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-muted-foreground text-xs">+ Cash Flow total</p>
              <p className="font-mono font-semibold tabular-nums">{formatCurrency(c.cf_total)}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-muted-foreground text-xs">= Tesorería calculada</p>
              <p className="font-mono font-semibold tabular-nums">{formatCurrency(c.saldo_final_calculado)}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-muted-foreground text-xs">Tesorería según balance</p>
              <p className="font-mono font-semibold tabular-nums">{formatCurrency(c.tesoreria_balance)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
